import { useMutation, UseMutationResult, UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createPortfolio,
  createPortfolioPositionReview,
  deletePortfolio,
  fetchPortfolio,
  fetchPortfolioPositionDetail,
  fetchPortfolioPositionReviews,
  fetchWatchlistLiveQuotes,
  updatePortfolio,
  updatePortfolioPositionDetail,
  Portfolio,
  PortfolioPositionDetail,
  PortfolioPositionDetailUpsertRequest,
  PortfolioResponse,
  PortfolioReviewEntry,
  PortfolioReviewEntryCreateRequest,
  PortfolioReviewListResponse,
  PortfolioUpsertRequest,
  WatchlistLiveQuotesResponse,
} from "@features/investments/api";
import { INVESTMENTS_QUOTE_POLL_MS } from "@features/investments/state/constants";

export const portfolioQueryKey = ["investments", "portfolio"] as const;
export const watchlistQueryKey = portfolioQueryKey;
export const watchlistLiveQuotesQueryKey = (tickers: string[]) => ["investments", "watchlist", "live-quotes", ...tickers] as const;
export const portfolioPositionDetailQueryKey = (portfolioID: number, ticker: string) => ["investments", "portfolio", portfolioID, ticker, "detail"] as const;
export const portfolioPositionReviewsQueryKey = (portfolioID: number, ticker: string) => ["investments", "portfolio", portfolioID, ticker, "reviews"] as const;

export function useInvestmentsPortfolioQuery(enabled = true): UseQueryResult<PortfolioResponse, Error> {
  return useQuery({
    queryKey: portfolioQueryKey,
    queryFn: fetchPortfolio,
    enabled,
    retry: false,
  });
}

export function useInvestmentsWatchlistQuery(enabled = true): UseQueryResult<PortfolioResponse, Error> {
  return useInvestmentsPortfolioQuery(enabled);
}

export function useWatchlistLiveQuotesQuery(tickers: string[], enabled = true): UseQueryResult<WatchlistLiveQuotesResponse, Error> {
  return useQuery({
    queryKey: watchlistLiveQuotesQueryKey(tickers),
    queryFn: () => fetchWatchlistLiveQuotes(tickers),
    enabled: enabled && tickers.length > 0,
    retry: false,
    refetchInterval: enabled ? INVESTMENTS_QUOTE_POLL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function usePortfolioPositionDetailQuery(
  portfolioID: number | null,
  ticker: string | null,
  enabled = true,
): UseQueryResult<PortfolioPositionDetail, Error> {
  return useQuery({
    queryKey: portfolioPositionDetailQueryKey(portfolioID ?? 0, ticker ?? ""),
    queryFn: () => fetchPortfolioPositionDetail(portfolioID as number, ticker as string),
    enabled: enabled && portfolioID != null && ticker != null && ticker.trim() !== "",
    retry: false,
  });
}

export function usePortfolioPositionReviewsQuery(
  portfolioID: number | null,
  ticker: string | null,
  enabled = true,
): UseQueryResult<PortfolioReviewListResponse, Error> {
  return useQuery({
    queryKey: portfolioPositionReviewsQueryKey(portfolioID ?? 0, ticker ?? ""),
    queryFn: () => fetchPortfolioPositionReviews(portfolioID as number, ticker as string),
    enabled: enabled && portfolioID != null && ticker != null && ticker.trim() !== "",
    retry: false,
  });
}

export function useCreatePortfolioMutation(): UseMutationResult<Portfolio, Error, PortfolioUpsertRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PortfolioUpsertRequest) => createPortfolio(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
    },
  });
}

export function useUpdatePortfolioMutation(): UseMutationResult<Portfolio, Error, { portfolioID: number; payload: PortfolioUpsertRequest; }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ portfolioID, payload }: { portfolioID: number; payload: PortfolioUpsertRequest; }) => updatePortfolio(portfolioID, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
    },
  });
}

export function useDeletePortfolioMutation(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (portfolioID: number) => deletePortfolio(portfolioID),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
    },
  });
}

export function useUpdatePortfolioPositionDetailMutation(): UseMutationResult<
  PortfolioPositionDetail,
  Error,
  { portfolioID: number; ticker: string; payload: PortfolioPositionDetailUpsertRequest; }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ portfolioID, ticker, payload }) => updatePortfolioPositionDetail(portfolioID, ticker, payload),
    onSuccess: async (_detail, variables) => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      await queryClient.invalidateQueries({ queryKey: portfolioPositionDetailQueryKey(variables.portfolioID, variables.ticker) });
    },
  });
}

export function useCreatePortfolioPositionReviewMutation(): UseMutationResult<
  PortfolioReviewEntry,
  Error,
  { portfolioID: number; ticker: string; payload: PortfolioReviewEntryCreateRequest; }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ portfolioID, ticker, payload }) => createPortfolioPositionReview(portfolioID, ticker, payload),
    onSuccess: async (_review, variables) => {
      await queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      await queryClient.invalidateQueries({ queryKey: portfolioPositionDetailQueryKey(variables.portfolioID, variables.ticker) });
      await queryClient.invalidateQueries({ queryKey: portfolioPositionReviewsQueryKey(variables.portfolioID, variables.ticker) });
    },
  });
}

export const useCreateWatchlistPortfolioMutation = useCreatePortfolioMutation;
export const useUpdateWatchlistPortfolioMutation = useUpdatePortfolioMutation;
export const useDeleteWatchlistPortfolioMutation = useDeletePortfolioMutation;
